import * as THREE from 'three';

/** Drawn only while the shadow map renders: `castOnlyIntoShadows` lets the shadow pass see it, the ordinary view never does. */
export const SHADOW_CASTER_LAYER = 30;
/** Ground tile, in metres, of one merged caster: small enough that the sun's camera-relative frustum still culls most of the map. */
const TILE = 24;
/** A surface this large is already one draw; copying it would only cost memory. */
const MAX_VERTICES = 20_000;
const flipped = { [THREE.FrontSide]: THREE.BackSide, [THREE.BackSide]: THREE.FrontSide, [THREE.DoubleSide]: THREE.DoubleSide } as const;

/**
 * Still scenery draws into the sun's shadow map one mesh at a time, and the low sun's long frustum takes in far more of the
 * map than the view does: about 740 shadow draws in the camp against 149 ordinary ones. This copies every frozen,
 * plain shadow caster under `roots` into one position-only mesh per ground tile and shadow side, in world space, and
 * stops the originals casting. The depth written is the same geometry with the same sides, so the shadows are unchanged.
 * Casters the default depth material would not draw plainly (alpha cutouts, displacement, custom depth, morphs,
 * multi-material) or that can move (an animated transform anywhere above them) keep their own draw.
 */
export function batchSceneryShadows(scene: THREE.Scene, roots: Iterable<THREE.Object3D>) {
  const tiles = new Map<string, THREE.Mesh[]>();
  const visit = (object: THREE.Object3D) => {
    if (!object.visible || object.matrixWorldAutoUpdate) return;
    const mesh = object as THREE.Mesh;
    if (mesh.isMesh && plainCaster(mesh)) {
      const material = mesh.material as THREE.Material, side = material.shadowSide ?? flipped[material.side];
      const centre = mesh.geometry.boundingSphere!.center.clone().applyMatrix4(mesh.matrixWorld);
      const key = `${side}:${Math.floor(centre.x / TILE)}:${Math.floor(centre.z / TILE)}`;
      let list = tiles.get(key); if (!list) tiles.set(key, list = []); list.push(mesh);
    }
    for (const child of object.children) visit(child);
  };
  for (const root of roots) visit(root);
  const materials = new Map<THREE.Side, THREE.Material>();
  const batches: THREE.Mesh[] = [];
  for (const [key, meshes] of tiles) {
    if (meshes.length < 2) continue;
    const side = Number(key.split(':')[0]) as THREE.Side;
    let material = materials.get(side);
    if (!material) materials.set(side, material = new THREE.MeshBasicMaterial({ colorWrite: false, side: flipped[side], shadowSide: side }));
    const batch = new THREE.Mesh(mergeWorldPositions(meshes), material);
    batch.name = 'scenery-shadows'; batch.castShadow = true; batch.receiveShadow = false; batch.layers.set(SHADOW_CASTER_LAYER);
    batch.matrixAutoUpdate = false; batch.matrixWorldAutoUpdate = false; batch.updateMatrixWorld(true);
    for (const mesh of meshes) mesh.castShadow = false;
    scene.add(batch); batches.push(batch);
  }
  return batches;
}

function plainCaster(mesh: THREE.Mesh) {
  if (!mesh.castShadow || (mesh as THREE.SkinnedMesh).isSkinnedMesh || (mesh as THREE.InstancedMesh).isInstancedMesh || mesh.customDepthMaterial || mesh.morphTargetInfluences) return false;
  const material = mesh.material, geometry = mesh.geometry, position = geometry.attributes.position;
  if (Array.isArray(material) || !material.visible || material.alphaTest > 0 || material.alphaToCoverage) return false;
  const standard = material as THREE.MeshStandardMaterial;
  if (standard.wireframe || (standard.displacementMap && standard.displacementScale !== 0)) return false;
  if (material.clippingPlanes?.length || !position || position.count > MAX_VERTICES || (position as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) return false;
  if (geometry.drawRange.start !== 0 || geometry.drawRange.count !== Infinity) return false;
  if (!geometry.boundingSphere) geometry.computeBoundingSphere();
  return true;
}

function mergeWorldPositions(meshes: THREE.Mesh[]) {
  let vertices = 0, indices = 0;
  for (const { geometry } of meshes) { vertices += geometry.attributes.position.count; indices += geometry.index?.count ?? geometry.attributes.position.count; }
  const positions = new Float32Array(vertices * 3), index = new Uint32Array(indices), point = new THREE.Vector3();
  let v = 0, i = 0;
  for (const mesh of meshes) {
    const { geometry, matrixWorld } = mesh, source = geometry.attributes.position, base = v;
    for (let k = 0; k < source.count; k++) { point.fromBufferAttribute(source, k).applyMatrix4(matrixWorld); positions[v * 3] = point.x; positions[v * 3 + 1] = point.y; positions[v * 3 + 2] = point.z; v++; }
    // A mirrored transform turns triangles inside out; swap two corners so single-sided faces keep facing the same way.
    const mirrored = matrixWorld.determinant() < 0, count = geometry.index?.count ?? source.count;
    for (let k = 0; k < count; k += 3) {
      const a = geometry.index ? geometry.index.getX(k) : k, b = geometry.index ? geometry.index.getX(k + 1) : k + 1, c = geometry.index ? geometry.index.getX(k + 2) : k + 2;
      index[i++] = base + a; index[i++] = base + (mirrored ? c : b); index[i++] = base + (mirrored ? b : c);
    }
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3)); merged.setIndex(new THREE.BufferAttribute(index, 1));
  merged.computeBoundingSphere();
  return merged;
}

const wrapped = new WeakSet<THREE.WebGLRenderer>();
/** The ordinary view leaves `SHADOW_CASTER_LAYER` out; the shadow pass tests the same camera, so it is let in only while shadows draw. */
export function castOnlyIntoShadows(renderer: THREE.WebGLRenderer) {
  if (wrapped.has(renderer)) return;
  wrapped.add(renderer);
  const shadowMap = renderer.shadowMap, render = shadowMap.render.bind(shadowMap);
  shadowMap.render = (lights, scene, camera) => {
    const layers = camera.layers.mask;
    camera.layers.enable(SHADOW_CASTER_LAYER);
    try { render(lights, scene, camera); } finally { camera.layers.mask = layers; }
  };
}
